/**
 * La spiegazione di una parola, dove la parola sta.
 *
 * **Non e' un `title=`, e la differenza non e' di gusto.** Il titolo nativo non
 * compare su uno schermo tattile, non si apre da tastiera, sparisce da solo
 * dopo qualche secondo e una parte dei lettori di schermo non lo annuncia:
 * cioe' proprio per la persona per cui questa spiegazione esiste, non esiste.
 *
 * **Si apre, non si sorvola.** Un pannello che appare al passaggio del mouse
 * copre cio' che si stava guardando nel momento peggiore, e su una tabella si
 * accende da solo mentre l'occhio scorre. Qui c'e' un gesto: si chiede, e si
 * chiude con `Esc` o cliccando fuori.
 *
 * **Sta sopra tutto, e non dentro.** Il pannello della decisione scorre
 * (`overflow-y: auto`), e in un contenitore che scorre un riquadro posizionato
 * in modo assoluto viene tagliato dal bordo: la spiegazione ci sarebbe e non si
 * leggerebbe. Per questo la nota e' `fixed` e prende le sue coordinate dal
 * segno che l'ha aperta. Il prezzo e' che scorrendo la finestra la nota
 * resterebbe ferma dove non deve — quindi uno scorrimento la chiude, invece di
 * lasciarla galleggiare lontano dalla parola che spiega.
 *
 * **Non si mette dappertutto.** Una spiegazione accanto all'ovvio svaluta
 * quella accanto all'importante: se il segno «?» sta anche vicino al bottone
 * «Entra», dopo due giorni nessuno lo vede piu'. Sta dove la parola non ha un
 * equivalente nella lingua di tutti i giorni.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { conceptSays, conceptTerm, type Concept } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'

/** Larghezza della nota e distacco dal segno: le stesse cifre del foglio di
 *  stile, che qui servono a tenerla dentro lo schermo. */
const WIDTH_M = 260
const MARGIN = 4

export function Hint({ about }: { about: Concept }): JSX.Element {
  const t = useT()
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const panel = useId()
  const trigger = useRef<HTMLButtonElement | null>(null)
  const box = useRef<HTMLSpanElement | null>(null)
  const term = conceptTerm(t, about)
  const open = at !== null

  const close = (): void => setAt(null)

  const show = (): void => {
    const rect = trigger.current?.getBoundingClientRect()
    if (rect === undefined) return
    // A destra del segno finche' ci sta, poi rientra: una nota che esce dallo
    // schermo e' una nota che non c'e'.
    const left = Math.min(rect.left, Math.max(WIDTH_M / 2, window.innerWidth - WIDTH_M - MARGIN))
    setAt({ top: rect.bottom + MARGIN, left: Math.max(MARGIN, left) })
  }

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      // Il fuoco torna da dove era partito: chi si e' aperto una spiegazione
      // con la tastiera non deve poi ritrovare la strada per conto suo.
      close()
      trigger.current?.focus()
    }
    const onOutside = (event: MouseEvent): void => {
      if (box.current && !box.current.contains(event.target as Node)) close()
    }
    // Terzo argomento vero: lo scorrimento di un contenitore interno non sale
    // fino a `document` se non lo si cattura, ed e' proprio quello — il
    // pannello della decisione — che sposterebbe la parola sotto la nota.
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <span className="hint" ref={box}>
      <button
        ref={trigger}
        type="button"
        className="hint-chiave"
        aria-expanded={open}
        aria-controls={panel}
        // Il nome dice di che cosa parla. «?» da solo, ripetuto otto volte
        // nella stessa schermata, e' otto bottoni identici per chi ascolta.
        aria-label={t('hint.about', { term })}
        onClick={() => (open ? close() : show())}
      >
        <span aria-hidden="true">?</span>
      </button>
      {at !== null && (
        <span
          className="hint-nota"
          id={panel}
          role="note"
          style={{ top: `${at.top}px`, left: `${at.left}px` }}
        >
          <strong>{term}</strong>
          <span>{conceptSays(t, about)}</span>
          {/* La spiegazione breve risponde a «che faccio adesso»; chi vuole
              «come sta tutto insieme» trova la voce nel glossario, alla sua
              ancora e non in cima alla pagina. */}
          <Link to={`/glossario#${about}`} className="hint-oltre">
            {t('hint.inGlossary')} →
          </Link>
        </span>
      )}
    </span>
  )
}
